import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Check if the browser supports the Web Speech API
 */
const getSpeechRecognition = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

/**
 * Hook for voice dictation using the Web Speech API
 * Provides speech-to-text transcription with audio stream for visualization
 */
export function useVoiceDictation() {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState(null);
  const [audioStream, setAudioStream] = useState(null);

  const recognitionRef = useRef(null);
  const audioStreamRef = useRef(null);
  const shouldRestartRef = useRef(false);

  // Check browser support
  const SpeechRecognition = getSpeechRecognition();
  const isSupported = !!SpeechRecognition;

  /**
   * Stop and clean up audio stream
   */
  const stopAudioStream = useCallback(() => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
      setAudioStream(null);
    }
  }, []);

  /**
   * Stop speech recognition
   */
  const stopRecognition = useCallback(() => {
    shouldRestartRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // Ignore errors when stopping
      }
    }
  }, []);

  /**
   * Start recording - initializes speech recognition and audio stream
   */
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError("Speech recognition is not supported in this browser");
      return;
    }

    setError(null);
    setTranscript("");
    setInterimTranscript("");

    try {
      // Get audio stream for visualizer
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      audioStreamRef.current = stream;
      setAudioStream(stream);

      // Initialize speech recognition
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsRecording(true);
      };

      recognition.onresult = (event) => {
        let finalText = "";
        let interimText = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const text = result[0].transcript;

          if (result.isFinal) {
            finalText += text;
          } else {
            interimText += text;
          }
        }

        if (finalText) {
          setTranscript((prev) => prev + finalText);
        }
        setInterimTranscript(interimText);
      };

      recognition.onerror = (event) => {
        if (event.error === "not-allowed" || event.error === "service-not-allowed") {
          setError("Microphone permission denied");
          stopAudioStream();
          setIsRecording(false);
        } else if (event.error === "no-speech") {
          // Ignore no-speech errors, just continue listening
        } else if (event.error !== "aborted") {
          setError(`Speech recognition error: ${event.error}`);
        }
      };

      recognition.onend = () => {
        // Auto-restart if we should continue recording
        if (shouldRestartRef.current) {
          try {
            recognition.start();
          } catch {
            // If restart fails, stop recording
            setIsRecording(false);
            stopAudioStream();
          }
        } else {
          setIsRecording(false);
        }
      };

      recognitionRef.current = recognition;
      shouldRestartRef.current = true;
      recognition.start();
    } catch (err) {
      setError(err.message || "Failed to start recording");
      stopAudioStream();
      setIsRecording(false);
    }
  }, [isSupported, stopAudioStream]);

  /**
   * Stop recording - stops speech recognition and audio stream
   */
  const stopRecording = useCallback(() => {
    stopRecognition();
    stopAudioStream();
    setIsRecording(false);
    setInterimTranscript("");
  }, [stopRecognition, stopAudioStream]);

  /**
   * Toggle recording on/off
   */
  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  /**
   * Clear the transcript
   */
  const clearTranscript = useCallback(() => {
    setTranscript("");
    setInterimTranscript("");
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecognition();
      stopAudioStream();
    };
  }, [stopRecognition, stopAudioStream]);

  return {
    // State
    isRecording,
    isSupported,
    transcript,
    interimTranscript,
    fullTranscript: transcript + interimTranscript,
    error,
    audioStream,

    // Actions
    startRecording,
    stopRecording,
    toggleRecording,
    clearTranscript,
  };
}

export default useVoiceDictation;
